import { Download, Eye, RotateCcw } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { usePermission } from '../../../../auth/AuthContext';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import type { DataColumn } from '../../../../components/data-table/DataTable';
import { DataTablePanel } from '../../../../components/data-table/DataTablePanel';
import { useDataTable, type DataTableQuery } from '../../../../components/data-table/useDataTable';
import type { MenuItem } from '../../../../components/Menu';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { formatBytes } from '../../../../lib/formatBytes';
import type { RoutingFileApi, RoutingFileReadApi } from '../routingApi';
import type { RoutingAttachmentItem } from '../routingTypes';
import { AttachFilesModal } from './AttachFilesModal';
import { AttachmentViewer } from './AttachmentViewer';

/**
 * A document's files, and the one being read — the Preview tab.
 *
 * **The tab carries the module's `download` key, not `read`** — the Repository preview's rule:
 * knowing a file exists and being handed its contents are different privileges. The tab is drawn
 * even when there is nothing to show, and says so with the way to attach one.
 *
 * **The list is a server-paged table** with search and sort like every table of records; a row's
 * anchor puts that file in the viewer below. With nothing chosen, the first file on the page is
 * shown — the order they were filed in, so the document itself before its enclosures.
 *
 * **Nothing is fetched until this panel mounts**, and it mounts only while its tab is open.
 *
 * **Filing is the SENDER's** — Dispatching passes an api WITH its write half, and the panel offers
 * the `attach` verbs only then. The Inbox reads the same files and offers no write at all: a received dispatch's
 * enclosures are what was sent, and a recipient adding one would change what the sender said.
 */
export function RoutingPreviewPanel({
  recordId,
  trackingNumber,
  api,
  modulePrefix,
  isActive,
  onChanged,
}: {
  recordId: string;
  trackingNumber: string;
  /** With a write half (`attach`) for the sender's register; read-only for the Inbox. */
  api: RoutingFileReadApi | RoutingFileApi;
  /** `document-dispatching` or `document-inbox` — whose keys gate the files. */
  modulePrefix: 'document-dispatching' | 'document-inbox' | 'document-receiving';
  /** A deleted record takes no new file. */
  isActive: boolean;
  /** A file was attached, deleted or restored — the page refreshes its counts. */
  onChanged: () => void;
}) {
  const toast = useToast();
  const writes = 'attach' in api ? api : null;
  const writable = writes != null;
  const canDownload = usePermission(`${modulePrefix}.download`);

  const [showDeleted, setShowDeleted] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [attaching, setAttaching] = useState(false);
  const [confirming, setConfirming] = useState<{ kind: 'delete' | 'restore'; row: RoutingAttachmentItem } | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchPage = useCallback(
    (query: DataTableQuery, signal: AbortSignal) =>
      api.attachments(recordId, { ...query, status: showDeleted ? undefined : 'active' }, signal),
    [api, recordId, showDeleted],
  );

  const state = useDataTable<RoutingAttachmentItem>({ fetchPage, pageSize: 5, defaultSort: 'createdAt:asc' });

  // DERIVED, never synced in an effect: the chosen file while it is on the page, else the first.
  const selected = state.rows.find((r) => r.id === selectedId) ?? state.rows[0] ?? null;

  const download = useCallback(
    async (row: RoutingAttachmentItem) => {
      try {
        const { blob, fileName } = await api.download(recordId, row.id);

        // Re-typed to the VERIFIED type, so a mislabelled payload cannot pick its own handler.
        const url = URL.createObjectURL(new Blob([blob], { type: row.contentType }));
        const anchor = window.document.createElement('a');

        anchor.href = url;
        anchor.download = fileName ?? row.fileName;
        window.document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();

        URL.revokeObjectURL(url);
      } catch (caught) {
        toast.failure(caught instanceof ApiError ? caught.message : 'Could not download the file.');
      }
    },
    [api, recordId, toast],
  );

  const rowActions = useCallback(
    (row: RoutingAttachmentItem): MenuItem[] => [
      {
        key: `view-${row.id}`,
        label: 'Preview',
        icon: <Eye className="h-4 w-4" aria-hidden="true" />,
        permission: `${modulePrefix}.download`,
        onClick: () => setSelectedId(row.id),
      },
      {
        key: `download-${row.id}`,
        label: 'Download',
        icon: <Download className="h-4 w-4" aria-hidden="true" />,
        permission: `${modulePrefix}.download`,
        onClick: () => void download(row),
      },
      ...(writable
        ? [
            {
              key: `restore-${row.id}`,
              label: 'Restore',
              icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
              permission: `${modulePrefix}.attach`,
              disabledReason: row.isActive ? 'This file is already active.' : null,
              onClick: () => setConfirming({ kind: 'restore', row }),
            },
          ]
        : []),
    ],
    [download, modulePrefix, writable],
  );

  const columns = useMemo(() => COLUMNS, []);

  async function runConfirmed() {
    if (!confirming || !writes) return;

    setRunning(true);
    setError(null);

    try {
      if (confirming.kind === 'delete') await writes.removeAttachment(recordId, confirming.row.id);
      else await writes.restoreAttachment(recordId, confirming.row.id);

      toast.success(`${confirming.kind === 'delete' ? 'Deleted' : 'Restored'} ${confirming.row.fileName}.`);
      setConfirming(null);
      state.reload();
      onChanged();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0));
    } finally {
      setRunning(false);
    }
  }

  return (
    <div className="flex min-h-full min-w-0 flex-col gap-4 p-4">
      <DataTablePanel<RoutingAttachmentItem>
        id={`${modulePrefix}-attachments`}
        state={state}
        columns={columns}
        rowKey={(row) => row.id}
        searchPlaceholder="Search file names"
        onCreate={writable ? () => setAttaching(true) : undefined}
        createLabel="Attach files"
        createPermission={`${modulePrefix}.attach`}
        createDisabledReason={isActive ? null : 'This document is deleted. Restore it before filing a file against it.'}
        settingsItems={
          writable
            ? [{ key: 'show-deleted', label: 'Show deleted', checked: showDeleted, onClick: () => setShowDeleted((v) => !v) }]
            : undefined
        }
        onRowOpen={(row) => setSelectedId(row.id)}
        openPermission={`${modulePrefix}.download`}
        rowActions={rowActions}
        onDelete={writable ? (row) => setConfirming({ kind: 'delete', row }) : undefined}
        deletePermission={`${modulePrefix}.attach`}
        deleteLabel={() => 'Delete'}
        deleteDisabledReason={(row) => (row.isActive ? null : 'This file is already deleted.')}
        otherInfo={(row) => row}
        emptyMessage={
          writable
            ? 'No file is attached — a hard copy kept on paper alone has nothing to show here. Attach a scan or the soft copy to read it on screen.'
            : 'The sending unit attached no file — a hard copy sent on paper alone has nothing to show here.'
        }
        emptySearchMessage="No file name matches that search."
      />

      {/* The viewer, drawn only for a reader who may be handed the contents. */}
      {canDownload && selected ? (
        <section
          aria-label={`Preview of ${selected.fileName}`}
          className="flex min-h-[520px] flex-1 flex-col overflow-hidden rounded-lg border border-slate-200 bg-white"
        >
          <header className="flex shrink-0 items-center gap-3 border-b border-slate-200 px-4 py-2">
            <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-800">{selected.fileName}</span>
            <span className="shrink-0 font-mono text-xs tabular-nums text-slate-500">{formatBytes(selected.sizeBytes)}</span>
          </header>

          <div className="min-h-0 flex-1">
            <AttachmentViewer
              key={selected.id}
              recordId={recordId}
              api={api}
              attachment={selected}
              downloadPermission={`${modulePrefix}.download`}
              onDownload={() => void download(selected)}
            />
          </div>
        </section>
      ) : null}

      {attaching && writes ? (
        <AttachFilesModal
          open
          recordId={recordId}
          trackingNumber={trackingNumber}
          api={writes}
          onClose={() => setAttaching(false)}
          onSaved={() => {
            setAttaching(false);
            state.reload();
            onChanged();
          }}
        />
      ) : null}

      {confirming ? (
        <ConfirmDialog
          open
          onClose={() => {
            setConfirming(null);
            setError(null);
          }}
          onConfirm={() => void runConfirmed()}
          title={confirming.kind === 'delete' ? 'Delete file' : 'Restore file'}
          danger={confirming.kind === 'delete'}
          requireConfirm={confirming.kind === 'delete'}
          requireCredential={confirming.kind === 'delete'}
          loading={running}
          id={`routing-attachment-${confirming.kind}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              <p>
                {confirming.kind === 'delete'
                  ? `${confirming.row.fileName} leaves this document's files. Its bytes are kept in the Repository, and it can be restored from this list with Show deleted.`
                  : `${confirming.row.fileName} returns to this document's files.`}
              </p>

              {error ? (
                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </div>
  );
}

const COLUMNS: DataColumn<RoutingAttachmentItem>[] = [
  {
    key: 'fileName',
    header: 'File',
    width: 320,
    sortable: true,
    render: (row) => <TextCell value={row.fileName} />,
  },
  {
    key: 'contentType',
    header: 'Type',
    width: 150,
    summary: true,
    render: (row) => <TextCell value={row.contentType} mono />,
  },
  {
    key: 'sizeBytes',
    header: 'Size',
    width: 110,
    align: 'right',
    summary: true,
    render: (row) => <TextCell value={formatBytes(row.sizeBytes)} mono />,
  },
  {
    key: 'status',
    header: 'Status',
    width: 110,
    status: true,
    render: (row) =>
      row.isActive ? <StatusText tone="active">Active</StatusText> : <StatusText tone="inactive">Deleted</StatusText>,
  },
];
